import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Upload, X as XIcon, ChevronDown, Search, AlertCircle, Edit, GripVertical, AlertTriangle } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Modal from '../Layout/Modal.jsx';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';
import { materialsAPI, recipesAPI, uploadAPI } from '../../utils/api';
import { getIngredientDetails, calculateIngredientCost, calculateUnitCost, calculateRecipeCostPerServing, formatCurrency } from '../../utils/calculations';
import { usePreventScroll } from '../../utils/hooks';
// Removed useStablePanelHeight import - no longer needed with fixed scrolling
import './RecipeForm.css';

// Function to extract public ID from Cloudinary URL (shared utility)
const extractPublicIdFromUrl = (url) => {
  if (!url) return null;
  try {
    // Extract public ID from Cloudinary URL
    // Format: https://res.cloudinary.com/cloud_name/image/upload/v123456789/folder/image.jpg
    const match = url.match(/\/upload\/[^\/]+\/(.+)$/);
    if (match) {
      let publicId = match[1];
      // Remove file extension from public ID (Cloudinary public IDs don't include extensions)
      publicId = publicId.replace(/\.[^/.]+$/, '');
      return publicId;
    }
    return null;
  } catch (error) {
    console.error('Error extracting public ID from URL:', error);
    return null;
  }
};

// Sortable Step Component
const SortableStep = ({ step, index, editingStep, editStepInstruction, setEditStepInstruction, editStepImage, setEditStepImage, editStepImagePreview, setEditStepImagePreview, uploadingEditStepImage, setUploadingEditStepImage, setFormData, setEditingStep, onViewFullImage, cleanupProcedureImage, editStepImagePublicId, setEditStepImagePublicId, originalEditStepImagePublicId, setOriginalEditStepImagePublicId, editStepImageRemoved, setEditStepImageRemoved, onRequestDeleteStep, pendingStepImagesRef }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative bg-card border border-border rounded-xl p-5 hover:border-primary/30 hover:shadow-sm transition-all duration-200 ${
        isDragging ? 'shadow-lg border-primary' : ''
      }`}
    >
      {/* Step Header */}
      <div className="flex items-start space-x-4">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 bg-muted hover:bg-muted/80 rounded border border-border hover:border-primary transition-all duration-200 shadow-sm hover:shadow-md self-center"
          title={t('recipeForm.dragToReorder')}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground hover:text-foreground" />
        </div>

        {/* Step Number */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold shadow-sm">
            {step.step_number}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 min-w-0">
          {editingStep === step.id ? (
            <div className="space-y-4">
              {/* Instruction Edit */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  {t('recipeForm.instruction')}
                </label>
                <textarea
                  value={editStepInstruction}
                  onChange={(e) => setEditStepInstruction(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
                  rows={2}
                  placeholder={t('recipeForm.enterStepInstruction')}
                />
              </div>

              {/* Image Section */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  {t('recipeForm.stepImage')}
                </label>
                
                {/* Current Image (or pending preview) */}
                {(step.image_url || step._previewUrl) && !editStepImagePreview && !editStepImageRemoved && (
                  <div className="flex items-center space-x-3 p-3 rounded-lg">
                    <img
                      src={step.image_url || step._previewUrl}
                      alt={`Step ${step.step_number}`}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    <div className="flex flex-col space-y-1">
                      <button
                        type="button"
                        onClick={() => onViewFullImage(step.image_url || step._previewUrl)}
                        className="text-primary hover:text-primary/80 text-xs font-medium"
                      >
{t('recipeForm.viewFullImage')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Only clear local state - don't delete from Cloudinary yet
                          // Deletion will happen when Save is clicked
                          console.log('🗑️ Remove step image button clicked');
                          setEditStepImage(null);
                          setEditStepImagePreview(null);
                          setEditStepImageRemoved(true); // Mark image as removed
                          // Keep originalEditStepImagePublicId so we know what to delete on Save
                          console.log('✅ Local state cleared. Image will be deleted from Cloudinary when Save is clicked.');
                        }}
                        className="text-destructive hover:text-destructive/80 text-xs font-medium"
                      >
{t('recipeForm.removeImage')}
                      </button>
                    </div>
                    <div className="flex-1">
                    </div>
                  </div>
                )}

                {/* New Image Preview */}
                {editStepImagePreview && (
                  <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg border border-border mb-2">
                    <img
                      src={editStepImagePreview}
                      alt={t('recipeForm.newStepPreview')}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground mb-2">New image selected</p>
                      <button
                        type="button"
                        onClick={() => {
                          // Only clear local state - don't delete from Cloudinary yet
                          // If it was uploaded, deletion will happen when Save is clicked
                          // If it's just selected (not uploaded), no cleanup needed
                          console.log('🗑️ Remove new step image preview button clicked');
                          setEditStepImage(null);
                          setEditStepImagePreview(null);
                          setEditStepImageRemoved(false); // Reset removed flag since we're removing new image
                          // Keep editStepImagePublicId if it exists (uploaded image) for cleanup on cancel
                          // Keep originalEditStepImagePublicId if it exists (original image) for deletion on Save
                          console.log('✅ Local state cleared. Image will be handled when Save is clicked.');
                        }}
                        className="text-destructive hover:text-destructive/80 text-sm font-medium"
                      >
{t('recipeForm.removeImage')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Image Upload */}
                <div className="flex items-center space-x-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      // Validate file type
                      if (!file.type.startsWith('image/')) {
                        setErrorMessage(t('common.pleaseSelectImageFile'));
                        setErrorModalOpen(true);
                        e.target.value = ''; // Reset file input
                        return;
                      }

                      // Validate file size (2MB limit)
                      if (file.size > 2 * 1024 * 1024) {
                        setErrorMessage(t('common.imageSizeLimit'));
                        setErrorModalOpen(true);
                        e.target.value = ''; // Reset file input
                        return;
                      }

                      // Store the file for later upload (when Save is clicked)
                      setEditStepImage(file);
                      setEditStepImageRemoved(false); // Reset removed flag for new file

                      // Create a local preview using FileReader
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setEditStepImagePreview(reader.result); // This will be a data URL for preview
                      };
                      reader.onerror = () => {
                        setErrorMessage(t('common.failedToPreviewImage'));
                        setErrorModalOpen(true);
                      };
                      reader.readAsDataURL(file);
                      
                      console.log('📁 Step image file selected for preview:', file.name);
                      console.log('📁 Upload will happen when Save button is clicked');
                    }}
                    className="hidden"
                    id={`edit-step-image-upload-${step.id}`}
                  />
                  <label
                    htmlFor={`edit-step-image-upload-${step.id}`}
                    className={`inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${
                      uploadingEditStepImage || editStepImagePreview ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    {t('common.uploadImage')}
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Instruction Display */}
              <div>
                <p className="text-card-foreground leading-relaxed mt-2">
                  {step.instruction_en}
                </p>

                {/* Image Preview (saved URL or pending preview) */}
                {(step.image_url || step._previewUrl) && (
                  <div className="flex items-center space-x-3 p-3 rounded-lg">
                    <img
                      src={step.image_url || step._previewUrl}
                      alt={`Step ${step.step_number}`}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => onViewFullImage(step.image_url || step._previewUrl)}
                      className="text-primary hover:text-primary/80 text-xs font-medium"
                    >
{t('recipeForm.viewFullImage')}
                    </button>
                    <div className="flex-1">
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0">
          {editingStep === step.id ? (
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!editStepInstruction.trim()) {
                    setErrorMessage(t('recipeForm.pleaseEnterInstruction'));
                    setErrorModalOpen(true);
                    return;
                  }

                  // Step image will be uploaded when dish form Save is clicked
                  let imageUrl = step.image_url; // Default to original image

                  let previewUrl = step._previewUrl;
                  if (editStepImageRemoved) {
                    imageUrl = ''; // backend will delete old image when updating record
                    previewUrl = undefined;
                    if (step._previewUrl) try { URL.revokeObjectURL(step._previewUrl); } catch (_) {}
                  } else if (editStepImage) {
                    if (pendingStepImagesRef?.current) pendingStepImagesRef.current[step.id] = editStepImage;
                    imageUrl = ''; // Placeholder until form Save uploads it
                    if (step._previewUrl) try { URL.revokeObjectURL(step._previewUrl); } catch (_) {}
                    previewUrl = URL.createObjectURL(editStepImage);
                  }

                  setFormData(prev => ({
                    ...prev,
                    procedures: prev.procedures.map(s =>
                      s.id === step.id
                        ? { ...s, instruction_en: editStepInstruction.trim(), image_url: imageUrl, _previewUrl: previewUrl }
                        : s
                    )
                  }));

                  setEditingStep(null);
                  setEditStepInstruction('');
                  setEditStepImage(null);
                  setEditStepImagePreview(null);
                  setEditStepImagePublicId(null);
                  setOriginalEditStepImagePublicId(null);
                  setEditStepImageRemoved(false);
                }}
                className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
              >
                {t('recipeForm.save')}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Remove any pending file for this step (no upload happened yet)
                  if (pendingStepImagesRef?.current) delete pendingStepImagesRef.current[step.id];

                  setEditingStep(null);
                  setEditStepInstruction('');
                  setEditStepImage(null);
                  setEditStepImagePreview(null);
                  setEditStepImagePublicId(null);
                  setOriginalEditStepImagePublicId(null);
                  setEditStepImageRemoved(false);
                }}
                className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-card-foreground bg-muted hover:bg-muted/80 transition-colors"
              >
                {t('recipeForm.cancel')}
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => {
                  setEditingStep(step.id);
                  setEditStepInstruction(step.instruction_en);
                  setEditStepImage(null);
                  setEditStepImagePreview(null);
                  setEditStepImageRemoved(false); // Reset removed flag when starting edit
                  
                  // Extract and store the original procedure image's public ID
                  const originalPublicId = extractPublicIdFromUrl(step.image_url);
                  setOriginalEditStepImagePublicId(originalPublicId);
                  console.log('📸 Original procedure image public ID extracted:', originalPublicId);
                }}
                className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-card-foreground bg-muted hover:bg-muted/80 transition-colors"
              >
                <Edit className="h-4 w-4 mr-1" />
{t('recipeForm.edit')}
              </button>
              <button
                type="button"
                onClick={() => onRequestDeleteStep && onRequestDeleteStep(step)}
                className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-1" />
{t('recipeForm.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Ingredient Item Component
const IngredientItem = ({ ingredient, onRemove, onEdit, isNew }) => {
  const { t, i18n } = useTranslation();
  const [details, setDetails] = useState({ name: 'Loading...', unit: '', cost: 0 });
  const [cost, setCost] = useState(0);
  const [allergens, setAllergens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    const loadIngredientDetails = async () => {
      try {
        setLoading(true);
        console.log('Loading ingredient details for:', ingredient);
        
        const ingredientDetails = await getIngredientDetails(ingredient);
        const ingredientCost = await calculateIngredientCost(ingredient);
        
        console.log('Ingredient details:', ingredientDetails);
        console.log('Ingredient cost:', ingredientCost);
        
        setDetails(ingredientDetails);
        setCost(ingredientCost);
        
        // Fetch allergen information
        let allergenData = [];
        if (ingredient.type === 'rawMaterial' && ingredient.code) {
          try {
            const materialResponse = await materialsAPI.getById(ingredient.code);
            allergenData = materialResponse.allergens || [];
          } catch (error) {
            console.error('Error fetching material allergens:', error);
          }
        } else if (ingredient.type === 'recipe' && ingredient.code) {
          try {
            const recipeResponse = await recipesAPI.getById(ingredient.code);
            allergenData = recipeResponse.computed_allergens || [];
          } catch (error) {
            console.error('Error fetching recipe allergens:', error);
          }
        }
        
        setAllergens(allergenData);
      } catch (error) {
        console.error('Error loading ingredient details:', error);
        setDetails({ name: t('recipeForm.error'), unit: '', cost: 0 });
        setCost(0);
        setAllergens([]);
      } finally {
        setLoading(false);
      }
    };

    loadIngredientDetails();
  }, [ingredient]);

  useEffect(() => {
    if (isNew) {
      setJustAdded(true);
      const timer = setTimeout(() => setJustAdded(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  if (loading) {
    return (
      <div className="flex items-center justify-between p-5 bg-white border rounded-lg shadow-sm">
        <div className="flex-1">
          <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-6' : 'space-x-6'}`}>
            <span className="font-medium text-base">{t('common.loading')}</span>
                      <span className="text-sm text-gray-500">
            {ingredient.quantityUsed} {ingredient.unit || t('recipeForm.piece')}
            {ingredient.wastePercentage > 0 && (
              <span className={`text-orange-600 ${i18n.language === 'ar' ? 'mr-2' : 'ml-2'}`}>
                (+{ingredient.wastePercentage}% {t('recipeForm.waste')})
              </span>
            )}
          </span>
            <span className="text-sm text-gray-500">
              {ingredient.type === 'rawMaterial' ? t('recipeForm.material') : t('recipeForm.recipe')}
            </span>
          </div>
          <div className="mt-2">
            <div className="text-xs text-gray-400">{t('recipeForm.loadingAllergens')}</div>
          </div>
        </div>
        <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
          <span className="text-sm font-medium">{t('common.loading')}</span>
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600 hover:text-red-900 p-2 rounded-md hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between p-5 bg-card border border-border rounded-lg shadow-sm transition-all ${
      justAdded ? 'ring-2 ring-chart-3 animate-pulse' : ''
    }`}>
      <div className="flex-1">
        <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-6' : 'space-x-6'}`}>
          <span className={`font-medium text-base text-card-foreground ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
            {i18n.language === 'ar' ? (details.name_ar || details.name) : details.name}
          </span>
          <span className="text-sm text-muted-foreground">
            {ingredient.quantityUsed} {details.unit || t('recipeForm.piece')}
            {ingredient.wastePercentage > 0 && (
              <span className={`text-chart-4 ${i18n.language === 'ar' ? 'mr-2' : 'ml-2'}`}>
                (+{ingredient.wastePercentage}% {t('recipeForm.waste')})
              </span>
            )}
          </span>
          <span className="text-sm text-muted-foreground">
            {ingredient.type === 'rawMaterial' ? t('recipeForm.material') : t('recipeForm.recipe')}
          </span>
        </div>
        {/* Allergen Information */}
        <div className={`mt-2 flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          <span className="text-xs text-muted-foreground">{t('recipeForm.allergens')}:</span>
          {allergens && allergens.length > 0 ? (
            <AllergenBadge
              allergens={allergens}
              variant="warning"
              size="xs"
              showCount={true}
              maxDisplay={3}
            />
          ) : (
            <span className="text-xs text-muted-foreground">{t('common.none')}</span>
          )}
        </div>
      </div>
      <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
        <span className="text-sm font-medium text-card-foreground">
          {formatCurrency(cost)}
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="text-primary hover:text-primary/80 p-2 rounded-md hover:bg-primary/10 transition-colors"
          title={t('recipeForm.editQuantity')}
        >
          <Edit className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="text-destructive hover:text-destructive/80 p-2 rounded-md hover:bg-destructive/10 transition-colors"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const RecipeForm = ({ recipe, categories, onSave, onCancel }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    image_url: '',
    servings: '',
    portion_size: '',
    unit: '',
    category_id: '',
    holding_temperature: '',
    has_pricing: true,
    ingredients: [],
    procedures: []
  });
  const [errors, setErrors] = useState({});
  const [materials, setMaterials] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [newIngredient, setNewIngredient] = useState({
    code: '',
    quantityUsed: '',
    wastePercentage: '',
    type: 'rawMaterial'
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null); // Store the selected file for later upload
  const [uploading, setUploading] = useState(false);
  const [uploadController, setUploadController] = useState(null);
  const [uploadedImagePublicId, setUploadedImagePublicId] = useState(null);
  const [originalImagePublicId, setOriginalImagePublicId] = useState(null); // Track original image when editing
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic' | 'ingredients' | 'procedure'
  const imageDeletedRef = useRef(false); // Track if image was already deleted
  const fileInputRef = useRef(null); // Ref for file input to reset it after error
  const pendingStepImagesRef = useRef({}); // stepId -> File; sent as image_base64, backend uploads when inserting/updating
  const [newStepInstruction, setNewStepInstruction] = useState('');
  const [newStepImage, setNewStepImage] = useState(null); // Store File object for later upload on form Save
  const [newStepImagePreview, setNewStepImagePreview] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [editStepInstruction, setEditStepInstruction] = useState('');
  const [editStepImage, setEditStepImage] = useState(null); // Store File object for later upload
  const [editStepImagePreview, setEditStepImagePreview] = useState(null);
  const [editStepImagePublicId, setEditStepImagePublicId] = useState(null); // Track procedure image public ID
  const [originalEditStepImagePublicId, setOriginalEditStepImagePublicId] = useState(null); // Track original procedure image public ID
  const [editStepImageRemoved, setEditStepImageRemoved] = useState(false); // Track if image was removed during edit
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [uploadingNewStepImage, setUploadingNewStepImage] = useState(false);
  const [uploadingEditStepImage, setUploadingEditStepImage] = useState(false);
  // Custom dropdown state
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isUnitOpen, setIsUnitOpen] = useState(false);
  const [isIngredientCodeOpen, setIsIngredientCodeOpen] = useState(false);
  const modalContentRef = useRef(null);
  // Dropdown refs
  const categoryDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null);
  const ingredientCodeDropdownRef = useRef(null);
  
  // Tab panel refs for stable height measurement
  const basicTabRef = useRef(null);
  const ingredientsTabRef = useRef(null);
  const procedureTabRef = useRef(null);
  
  const tabRefs = {
    basic: basicTabRef,
    ingredients: ingredientsTabRef,
    procedure: procedureTabRef
  };
  
  // Removed useStablePanelHeight hook - no longer needed with fixed scrolling
  // Drag & drop panel state
  const [isAllMaterialsView, setIsAllMaterialsView] = useState(false);
  const [isRecipesView, setIsRecipesView] = useState(false);
  const [openCategoryIds, setOpenCategoryIds] = useState(new Set());
  const [materialSearchTerm, setMaterialSearchTerm] = useState('');
  const [pendingIngredient, setPendingIngredient] = useState(null); // { code, type }
  const [quantityPromptOpen, setQuantityPromptOpen] = useState(false);
  const [quantityInput, setQuantityInput] = useState('');
  const [wastePercentageInput, setWastePercentageInput] = useState('');
  const [lastAddedId, setLastAddedId] = useState(null);
  const [showSourcePanelMobile, setShowSourcePanelMobile] = useState(false);
  // Removed isDragOver state - no longer needed with click-based selection
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [editQuantityModalOpen, setEditQuantityModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [stepToDelete, setStepToDelete] = useState(null); // Step object when delete confirmation is open
  const [editTarget, setEditTarget] = useState(null); // { id, code, type, name, unit }
  const [editQuantityInput, setEditQuantityInput] = useState('');
  const [editWastePercentageInput, setEditWastePercentageInput] = useState('');
  const [ingredientMode, setIngredientMode] = useState('dragdrop'); // 'dropdown' | 'dragdrop'

  // Prevent background scrolling when modal is open
  usePreventScroll(isModalOpen);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for steps reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.procedures.findIndex(step => step.id === active.id);
        const newIndex = prev.procedures.findIndex(step => step.id === over.id);
        
        const newProcedures = arrayMove(prev.procedures, oldIndex, newIndex);
        
        // Update step numbers based on new order
        return {
          ...prev,
          procedures: newProcedures.map((step, index) => ({
            ...step,
            step_number: index + 1
          }))
        };
      });
    }
  };

  // Handle viewing full size image
  const handleViewFullImage = (imageUrl) => {
    // Try to get the original image by removing transformation parameters
    let originalUrl = imageUrl;
    
    // If it's a Cloudinary URL, try to get the original version
    if (imageUrl.includes('cloudinary.com')) {
      try {
        // Split the URL to get the base and path parts
        const urlParts = imageUrl.split('/upload/');
        if (urlParts.length === 2) {
          // Reconstruct without transformation parameters
          originalUrl = urlParts[0] + '/upload/' + urlParts[1];
          // Remove any transformation parameters (like /w_400,h_400,c_fill)
          originalUrl = originalUrl.replace(/\/w_\d+,h_\d+,c_\w+/g, '');
        }
      } catch (error) {
        console.error('Error processing Cloudinary URL:', error);
        // Fallback to original URL if processing fails
        originalUrl = imageUrl;
      }
    }
    
    setModalImageUrl(originalUrl);
    setImageModalOpen(true);
  };

  // Handle clicks outside modal content
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Allow file input clicks to work properly
      if (event.target.type === 'file' || event.target.closest('input[type="file"]')) {
        return;
      }
      
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        // Clicked outside modal content - prevent default behavior
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Removed handleScroll - scroll events are not preventable and can cause inconsistencies

    const handleWheel = (event) => {
      // Allow wheel scrolling within the modal content
      if (modalContentRef.current && modalContentRef.current.contains(event.target)) {
        // Don't prevent - let it scroll naturally
        return;
      }
      // Prevent wheel scrolling outside the modal
      event.preventDefault();
      event.stopPropagation();
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('wheel', handleWheel, { passive: false });

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Close custom dropdowns on outside click
  useEffect(() => {
    const handler = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) {
        setIsUnitOpen(false);
      }
      if (ingredientCodeDropdownRef.current && !ingredientCodeDropdownRef.current.contains(event.target)) {
        setIsIngredientCodeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOverlayClick = (event) => {
    // Allow file input clicks to work properly
    if (event.target.type === 'file' || event.target.closest('input[type="file"]')) {
      return;
    }
    
    // Prevent closing when clicking outside - do nothing
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    loadData();
  }, []);

  // Function to clean up procedure image from Cloudinary
  const cleanupProcedureImage = async (imagePublicId) => {
    if (imagePublicId) {
      try {
        console.log('🧹 Cleanup: Deleting procedure image:', imagePublicId);
        await uploadAPI.deleteImage(imagePublicId);
        console.log('✅ Cleanup: Procedure image cleaned up successfully');
      } catch (error) {
        console.error('❌ Cleanup: Failed to cleanup procedure image:', error);
      }
    }
  };

  // Clear pending step images when form is canceled (no upload happened yet)
  const clearPendingStepImages = () => {
    pendingStepImagesRef.current = {};
    setNewStepImage(null);
    setNewStepImagePreview(null);
  };

  // Function to clean up uploaded image from Cloudinary
  const cleanupUploadedImage = async () => {
    if (uploadedImagePublicId && !recipe?.image_url && !imageDeletedRef.current) {
      // Only delete if this is a new upload (not editing existing recipe) and not already deleted
      imageDeletedRef.current = true; // Mark as deleted immediately to prevent duplicate calls
      try {
        console.log('🧹 Cleanup: Deleting uploaded image:', uploadedImagePublicId);
        await uploadAPI.deleteImage(uploadedImagePublicId);
        console.log('✅ Cleanup: Image cleaned up successfully');
      } catch (error) {
        console.error('❌ Cleanup: Failed to cleanup image:', error);
        // Don't show error to user as this is cleanup
      }
    } else {
      console.log('ℹ️ Cleanup: Skipping image deletion (no uploaded image, editing existing, or already deleted)');
    }
    
    // Clear selectedFile if user cancels without saving
    // No cleanup needed for selectedFile since it's just a local file reference
    if (selectedFile) {
      setSelectedFile(null);
      setImagePreview(null);
    }
  };

  // Cleanup uploaded image when component unmounts (only for uploaded images, not selected files)
  useEffect(() => {
    return () => {
      if (uploadedImagePublicId && !recipe?.image_url && !imageDeletedRef.current) {
        // Cleanup on unmount if image was uploaded but not saved and not already cleaned up
        cleanupUploadedImage();
      }
    };
  }, []); // Only run on mount/unmount, not when uploadedImagePublicId changes

  useEffect(() => {
    if (recipe) {
      // If ingredients and procedures are missing, try to load them separately
      if (!recipe.ingredients && !recipe.procedures && recipe.id) {
        loadRecipeDetails(recipe.id).then(details => {
          // Transform ingredients from backend format to frontend format
          const transformedIngredients = (details.ingredients || []).map(ingredient => {
            // Determine the type and code based on which ID is present
            let type = 'rawMaterial';
            let code = '';
            
            if (ingredient.material_id) {
              type = 'rawMaterial';
              code = ingredient.material_id;
            } else if (ingredient.recipe_ingredient_id) {
              type = 'recipe';
              code = ingredient.recipe_ingredient_id;
            }
            
            const transformed = {
              id: ingredient.id,
              code: code,
              quantityUsed: ingredient.quantity,
              unit: ingredient.unit || 'piece',
              wastePercentage: ingredient.waste_percentage || 0,
              type: type
            };
            
            return transformed;
          });
          
          const newFormData = {
            name_en: recipe.name_en || '',
            name_ar: recipe.name_ar || '',
            image_url: recipe.image_url || '',
            servings: recipe.servings || '',
            portion_size: recipe.portion_size || '',
            unit: recipe.unit || '',
            category_id: recipe.category_id !== undefined ? recipe.category_id : '',
            holding_temperature: recipe.holding_temperature || '',
            has_pricing: recipe.has_pricing !== undefined ? recipe.has_pricing : true,
            ingredients: transformedIngredients,
            procedures: details.procedures || []
          };
          
          setFormData(newFormData);
          setImagePreview(recipe.image_url || null);
          
          // Extract and store the original image's public ID for potential deletion
          const originalPublicId = extractPublicIdFromUrl(recipe.image_url);
          setOriginalImagePublicId(originalPublicId);
          console.log('📸 Original image public ID extracted:', originalPublicId);
        });
      } else {
        // Use existing ingredients and procedures if available
        const transformedIngredients = (recipe.ingredients || []).map(ingredient => {
          // Determine the type and code based on which ID is present
          let type = 'rawMaterial';
          let code = '';
          
          if (ingredient.material_id) {
            type = 'rawMaterial';
            code = ingredient.material_id;
          } else if (ingredient.recipe_ingredient_id) {
            type = 'recipe';
            code = ingredient.recipe_ingredient_id;
          }
          
          const transformed = {
            id: ingredient.id,
            code: code,
            quantityUsed: ingredient.quantity,
            unit: ingredient.unit || 'piece',
            wastePercentage: ingredient.waste_percentage || 0,
            type: type
          };
          
          return transformed;
        });
        
        const newFormData = {
          name_en: recipe.name_en || '',
          name_ar: recipe.name_ar || '',
          image_url: recipe.image_url || '',
          servings: recipe.servings || '',
          portion_size: recipe.portion_size || '',
          unit: recipe.unit || '',
          category_id: recipe.category_id !== undefined ? recipe.category_id : '',
          holding_temperature: recipe.holding_temperature || '',
          has_pricing: recipe.has_pricing !== undefined ? recipe.has_pricing : true,
          ingredients: transformedIngredients,
          procedures: recipe.procedures || []
        };
        
        setFormData(newFormData);
        setImagePreview(recipe.image_url || null);
        
        // Extract and store the original image's public ID for potential deletion
        const originalPublicId = extractPublicIdFromUrl(recipe.image_url);
        setOriginalImagePublicId(originalPublicId);
        console.log('📸 Original image public ID extracted:', originalPublicId);
      }
    }
  }, [recipe]);

  const loadData = async () => {
    try {
      const [materialsData, recipesData, materialCategoriesData] = await Promise.all([
        materialsAPI.getAll(),
        recipesAPI.getAll(),
        materialsAPI.getCategories()
      ]);
      setMaterials(materialsData);
      setRecipes(recipesData);
      setMaterialCategories(materialCategoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert(t('recipeForm.failedToLoadData'));
    }
  };

  // Function to load recipe ingredients and procedures
  const loadRecipeDetails = async (recipeId) => {
    try {
      // Use the getById method which should return recipe with ingredients
      const recipeDetails = await recipesAPI.getById(recipeId);
      
      return {
        ingredients: recipeDetails.ingredients || [],
        procedures: recipeDetails.procedures || []
      };
    } catch (error) {
      console.error('Error loading recipe details:', error);
      return {
        ingredients: [],
        procedures: []
      };
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleIngredientChange = (e) => {
    const { name, value } = e.target;
    setNewIngredient(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const confirmAddIngredient = (code, type, quantity, wastePercentage = 0) => {
    if (!code || !quantity) return;

    // Validate the selected (code, type) pair against the correct catalog.
    // Materials and recipes share the numeric id space, so we MUST trust the
    // caller-supplied `type` instead of re-deriving it from `code` alone.
    const normalizedType = type === 'recipe' ? 'recipe' : 'rawMaterial';
    const exists = normalizedType === 'rawMaterial'
      ? materials.some(m => m.id === code)
      : recipes.some(r => r.id === code);
    if (!exists) {
      alert('Invalid code. Please select a valid material or dish.');
      return;
    }

    // Duplicate check must be type-aware so a material and a dish with the
    // same id can coexist in the same recipe.
    const isDuplicate = formData.ingredients.some(
      ingredient => ingredient.code === code && ingredient.type === normalizedType
    );
    if (isDuplicate) {
      setShowDuplicateModal(true);
      return;
    }

    const ingredient = {
      code,
      quantityUsed: quantity,
      wastePercentage: wastePercentage || 0,
      type: normalizedType,
      id: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };

    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, ingredient]
    }));
    setLastAddedId(ingredient.id);
  };

  const addIngredient = () => {
    if (!newIngredient.code || !newIngredient.quantityUsed) {
      return;
    }
    confirmAddIngredient(newIngredient.code, newIngredient.type, newIngredient.quantityUsed, newIngredient.wastePercentage);
    setNewIngredient({ code: '', quantityUsed: '', wastePercentage: '', type: 'rawMaterial' });
  };

  const removeIngredient = (ingredientId) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter(ingredient => ingredient.id !== ingredientId)
    }));
  };

  const resolveIngredientDisplay = (ingredient) => {
    if (!ingredient) return { name: t('recipeForm.ingredient'), unit: '' };
    if (ingredient.type === 'rawMaterial') {
      const m = materials.find(mm => mm.id === ingredient.code);
      return { name: m?.name_en || t('recipeForm.material'), unit: m?.sub_unit || ingredient.unit || '' };
    }
    const r = recipes.find(rr => rr.id === ingredient.code);
    return { name: r?.name_en || t('recipeForm.recipe'), unit: ingredient.unit || '' };
  };

  const openEditQuantity = (ingredient) => {
    const display = resolveIngredientDisplay(ingredient);
    setEditTarget({ id: ingredient.id, code: ingredient.code, type: ingredient.type, name: display.name, unit: display.unit });
    setEditQuantityInput(String(ingredient.quantityUsed || ''));
    setEditWastePercentageInput(String(ingredient.wastePercentage || ''));
    setEditQuantityModalOpen(true);
  };

  const confirmEditQuantity = () => {
    const parsed = parseFloat(editQuantityInput);
    const wasteParsed = parseFloat(editWastePercentageInput) || 0;
    if (Number.isNaN(parsed) || parsed <= 0 || !editTarget) return;
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map(ing => ing.id === editTarget.id ? { 
        ...ing, 
        quantityUsed: String(parsed),
        wastePercentage: wasteParsed
      } : ing)
    }));
    setEditQuantityModalOpen(false);
    setEditTarget(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrorMessage(t('recipeForm.pleaseSelectImageFile'));
      setErrorModalOpen(true);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage(t('common.imageSizeLimit'));
      setErrorModalOpen(true);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Store the file for later upload (when Save is clicked)
    setSelectedFile(file);
    imageDeletedRef.current = false; // Reset deletion flag for new file

    // Create a local preview using FileReader
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result); // This will be a data URL for preview
    };
    reader.onerror = () => {
      setErrorMessage(t('common.failedToPreviewImage'));
      setErrorModalOpen(true);
    };
    reader.readAsDataURL(file);
    
    console.log('📁 File selected for preview:', file.name);
    console.log('📁 Upload will happen when Save button is clicked');
  };

  const removeImage = () => {
    console.log('🗑️ Remove image button clicked');
    console.log('Current uploadedImagePublicId:', uploadedImagePublicId);
    console.log('Current originalImagePublicId:', originalImagePublicId);
    console.log('Current selectedFile:', selectedFile);
    console.log('Current recipe?.image_url:', recipe?.image_url);
    console.log('Current formData.image_url:', formData.image_url);
    console.log('Dish exists:', !!recipe);
    console.log('Is editing mode:', !!recipe?.image_url);
    
    // Only clear local state - don't delete from Cloudinary yet
    // Deletion will happen when Save is clicked
    console.log('🧹 Clearing local state (deletion from Cloudinary will happen on Save)...');
    setFormData(prev => ({
      ...prev,
      image_url: '' // Clear image URL so it gets saved as empty
    }));
    setImagePreview(null);
    setSelectedFile(null); // Clear selected file if removing image
    setUploadedImagePublicId(null);
    // Keep originalImagePublicId so we know what to delete on Save
    // Only clear it if there's no original image (new dish)
    if (!recipe?.image_url) {
      setOriginalImagePublicId(null);
    }
    imageDeletedRef.current = false; // Reset deletion flag
    console.log('✅ Local state cleared. Image will be deleted from Cloudinary when Save is clicked.');
  };



  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name_en.trim()) {
      newErrors.name_en = t('validation.required');
    }
    
    if (!formData.name_ar.trim()) {
      newErrors.name_ar = t('validation.required');
    }
    
    if (formData.servings && isNaN(formData.servings)) {
      newErrors.servings = t('validation.invalidNumber');
    }
    
    if (formData.portion_size && isNaN(formData.portion_size)) {
      newErrors.portion_size = t('validation.invalidNumber');
    }
    
    // Require at least one ingredient for new dishes
    if (!recipe && formData.ingredients.length === 0) {
      newErrors.ingredients = t('recipeForm.atLeastOneIngredientRequired');
    }
    
    // Validate procedures - check if any step has empty English instruction
    if (formData.procedures.length > 0) {
      const invalidSteps = formData.procedures.filter(step => !step.instruction_en.trim());
      if (invalidSteps.length > 0) {
        newErrors.procedures = t('recipeForm.allStepsMustHaveEnglishInstructions');
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== RECIPE FORM SUBMIT START ===');
    console.log(t('recipeForm.recipeFormSubmitTriggered'));
    console.log('Form data before validation:', formData);
    console.log('Selected file:', selectedFile ? selectedFile.name : 'none');
    
    try {
      const validationResult = validateForm();
      console.log('Validation result:', validationResult);
      
      if (!validationResult) {
        console.log(t('recipeForm.recipeFormValidationFailed'));
        console.log('Validation errors:', errors);
        // Focus the tab that contains the first error for better UX
        if (errors.ingredients) {
          setActiveTab('ingredients');
        } else if (errors.procedures) {
          setActiveTab('procedure');
        } else {
          setActiveTab('basic');
        }
        console.log('=== RECIPE FORM SUBMIT END (VALIDATION FAILED) ===');
        return;
      }
      
      console.log(t('recipeForm.recipeFormValidationPassed'));
      setUploading(true); // Disable Save button for entire submit (upload + insert/update)

      // Track uploaded image URL to include in submitData (React state updates are async)
      let uploadedImageUrl = formData.image_url;
      
      // If image was removed (image_url is empty but there was an original image), delete from Cloudinary
      if (!formData.image_url && originalImagePublicId && recipe?.image_url) {
        try {
          console.log('🗑️ Image was removed: Deleting from Cloudinary:', originalImagePublicId);
          await uploadAPI.deleteImage(originalImagePublicId);
          console.log('✅ Image deleted from Cloudinary successfully');
          // Clear originalImagePublicId after deletion
          setOriginalImagePublicId(null);
        } catch (deleteError) {
          console.error('❌ Error deleting image from Cloudinary:', deleteError);
          // Continue with save even if deletion fails - the URL will be cleared in DB
        }
      }
      
      // If there's a selected file, upload it first before saving
      if (selectedFile) {
        console.log('📤 Uploading image before saving dish...');
        setUploading(true);
        
        try {
          // If replacing an existing image (or removed image and now adding new one), delete the old one first
          const oldPublicId = originalImagePublicId || uploadedImagePublicId;
          if (oldPublicId) {
            try {
              console.log('🧹 Replacing/removing old image: Deleting from Cloudinary:', oldPublicId);
              await uploadAPI.deleteImage(oldPublicId);
              console.log('✅ Old image deleted from Cloudinary successfully');
              // Clear originalImagePublicId after deletion
              if (originalImagePublicId) {
                setOriginalImagePublicId(null);
              }
            } catch (deleteError) {
              console.error('❌ Error deleting old image from Cloudinary:', deleteError);
              // Continue with uploading new image even if old image deletion fails
            }
          }

          // Upload the selected file to Cloudinary
          const controller = new AbortController();
          setUploadController(controller);
          
          const result = await uploadAPI.uploadImage(selectedFile, controller.signal, 'recipes');
          console.log('✅ Image uploaded to Cloudinary:', result.imageUrl);
          
          // Store uploaded image URL for use in submitData
          uploadedImageUrl = result.imageUrl;
          
          // Update form data with uploaded image URL
          setFormData(prev => ({
            ...prev,
            image_url: result.imageUrl
          }));
          
          setUploadedImagePublicId(result.publicId);
          setImagePreview(result.imageUrl); // Update preview to use uploaded URL
          setSelectedFile(null); // Clear selected file since it's now uploaded
          imageDeletedRef.current = false;
          
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('Upload cancelled by user');
            return; // Don't proceed with save if upload was cancelled
          } else {
            console.error('Error uploading image:', error);
            setErrorMessage(error.message || t('common.failedToUploadImage'));
            setErrorModalOpen(true);
            setUploading(false);
            setUploadController(null);
            return; // Don't proceed with save if upload failed
          }
        } finally {
          setUploading(false);
          setUploadController(null);
        }
      } else if (!formData.image_url && originalImagePublicId && recipe?.image_url) {
        // If image was removed (no selectedFile, image_url is empty, but there was an original image)
        // Delete from Cloudinary and clear it from database
        try {
          console.log('🗑️ Image was removed: Deleting from Cloudinary:', originalImagePublicId);
          await uploadAPI.deleteImage(originalImagePublicId);
          console.log('✅ Image deleted from Cloudinary successfully');
          // Clear originalImagePublicId after deletion
          setOriginalImagePublicId(null);
        } catch (deleteError) {
          console.error('❌ Error deleting image from Cloudinary:', deleteError);
          // Continue with save even if deletion fails - the URL will be cleared in DB
        }
      }

      // Build procedures with image_base64 for steps that have a pending file (backend will upload when inserting/updating)
      const fileToDataUrl = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const pendingStepImages = pendingStepImagesRef.current || {};
      const proceduresForSubmit = await Promise.all((formData.procedures || []).map(async (step) => {
        const file = pendingStepImages[step.id];
        const payload = {
          id: step.id,
          step_number: step.step_number,
          instruction_en: step.instruction_en,
          instruction_ar: step.instruction_ar || '',
          image_url: step.image_url || ''
        };
        if (file) {
          try {
            payload.image_base64 = await fileToDataUrl(file);
            payload.image_url = ''; // backend will set from upload
          } catch (err) {
            console.error('Failed to read step image:', err);
          }
        }
        return payload;
      }));
      pendingStepImagesRef.current = {};
      
      const submitData = {
        ...formData,
        image_url: uploadedImageUrl,
        procedures: proceduresForSubmit
      };
      
      console.log('📤 Submit data includes image_url:', submitData.image_url);
      console.log('Submitting dish data:', submitData);
      console.log('About to call onSave function...');
      console.log('onSave function:', typeof onSave);
      
      await Promise.resolve(onSave(submitData)); // await so Save stays disabled until API completes
      console.log('onSave function called successfully');
      // Revoke pending step preview object URLs to avoid leaks
      (formData.procedures || []).forEach(s => {
        if (s._previewUrl) try { URL.revokeObjectURL(s._previewUrl); } catch (_) {}
      });
    } catch (error) {
      console.error('Error in recipe handleSubmit:', error);
    } finally {
      setUploading(false); // Re-enable Save button (e.g. if save failed or modal stays open)
    }
    
    console.log('=== RECIPE FORM SUBMIT END ===');
    
    // Clear the uploaded image tracking since form was saved successfully
    setUploadedImagePublicId(null);
    setOriginalImagePublicId(null);
  };

  const unitOptions = ['g', 'ml', 'piece', 'order'];

  const getAvailableItems = () => {
    const materialOptions = materials.map(material => ({
      id: material.id,
      name: material.name_en,
      type: 'rawMaterial'
    }));
    
    const recipeOptions = recipes.map(recipe => ({
      id: recipe.id,
      name: recipe.name_en,
      type: 'recipe'
    }));
    
    return [...materialOptions, ...recipeOptions];
  };

  // Selection handler for materials library
  const handleMaterialSelect = (item) => {
    const selectedType = item.type === 'recipe' ? 'recipe' : 'rawMaterial';
    setPendingIngredient({ code: item.id, type: selectedType, name: item.name, unit: item.unit });
    setQuantityInput('');
    setWastePercentageInput('');
    setQuantityPromptOpen(true);
  };

  // Removed drag handlers - now using click-based selection

  const toggleCategoryOpen = (categoryId) => {
    setOpenCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId); else next.add(categoryId);
      return next;
    });
  };

  const filteredMaterials = materials.filter(m =>
    m.name_en?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    m.name_ar?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    String(m.id).includes(materialSearchTerm)
  );
  const filteredRecipesList = recipes.filter(r =>
    r.name_en?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    r.name_ar?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    String(r.id).includes(materialSearchTerm)
  );
  const filteredMaterialCategories = materialCategories.filter(cat =>
    cat.name_en?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    cat.name_ar?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    String(cat.id).includes(materialSearchTerm)
  );
  const filteredRecipeCategories = categories.filter(cat =>
    cat.name_en?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    cat.name_ar?.toLowerCase().includes(materialSearchTerm.toLowerCase()) ||
    String(cat.id).includes(materialSearchTerm)
  );

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="recipe-form-shell modal-content" ref={modalContentRef}>
        {/* Header with better spacing */}
        <div className="flex justify-between items-center pt-0 pb-0 px-6">
          <h2 className="text-2xl font-semibold text-card-foreground">
            {recipe ? t('recipes.editDish') : t('recipes.addNewDish')}
          </h2>
          <button
            onClick={() => {
              // Don't await cleanup - let it run in background
              cleanupUploadedImage();
              clearPendingStepImages();
              setIsModalOpen(false);
              onCancel();
            }}
            className="text-muted-foreground hover:text-card-foreground p-2 rounded-md hover:bg-muted transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs with better spacing */}
        <div className="px-6 pt-0 pb-0 border-b border-border">
          <div className="flex bg-muted rounded-lg p-1 w-full max-w-lg mx-auto">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'basic' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
{t('recipeForm.recipeOverview')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('ingredients')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'ingredients' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
{t('recipeForm.ingredients')}
              {formData.ingredients.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center text-xs px-2 py-0.5 rounded-full bg-muted text-card-foreground">
                  {formData.ingredients.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('procedure')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'procedure' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
{t('recipeForm.procedure')}
              {formData.procedures.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center text-xs px-2 py-0.5 rounded-full bg-muted text-card-foreground">
                  {formData.procedures.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          {/* Tab Panels Viewport */}
          <div 
            className="tab-panels-viewport"
            style={{ height: '100%' }}
          >
            {/* Recipe Overview Tab */}
            {activeTab === 'basic' && (
              <div 
                ref={basicTabRef}
                className="tab-panel is-active p-6 flex flex-col"
              >
              <div className="max-w-7xl mx-auto w-full space-y-4">
                {/* Page Header removed */}

                {/* Recipe Details Card */}
                <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-card-foreground">{t('recipeForm.recipeDetails')}</h4>
                      <p className="text-sm text-muted-foreground">{t('recipeForm.basicInformation')}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Recipe Name English */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
                        {t('recipes.dishNameEn')} <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        name="name_en"
                        value={formData.name_en}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                          errors.name_en ? 'border-destructive bg-destructive/10' : 'border-border hover:border-primary'
                        }`}
                        placeholder={t('recipeForm.enterDishNameEnglish')}
                      />
                      {errors.name_en && (
                        <p className="text-sm text-destructive flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.name_en}
                        </p>
                      )}
                    </div>

                    {/* Recipe Name Arabic */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
                        {t('recipes.dishNameAr')} <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        name="name_ar"
                        value={formData.name_ar}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                          errors.name_ar ? 'border-destructive bg-destructive/10' : 'border-border hover:border-primary'
                        }`}
                        placeholder={t('recipeForm.enterDishNameArabic')}
                      />
                      {errors.name_ar && (
                        <p className="text-sm text-destructive flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.name_ar}
                        </p>
                      )}
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
                        {t('common.category')}
                      </label>
                      <div className="relative" ref={categoryDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                          className="w-full px-4 py-3 border border-border rounded-lg bg-background hover:border-primary focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-left flex items-center justify-between"
                        >
                          <span className={`${formData.category_id ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {formData.category_id ? categories.find(cat => cat.id === formData.category_id)?.name_en : t('recipeForm.selectCategory')}
                          </span>
                          <svg className={`w-5 h-5 text-muted-foreground transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isCategoryOpen && (
                          <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            <div className="py-2">
                              <button
                                type="button"
                                onClick={() => { setFormData(prev => ({ ...prev, category_id: '' })); setIsCategoryOpen(false); }}
                                className="w-full text-left px-4 py-2 text-sm text-card-foreground hover:bg-muted transition-colors"
                              >
                                {t('recipeForm.selectCategory')}
                              </button>
                              {categories.map(category => (
                                <button
                                  key={category.id}
                                  type="button"
                                  onClick={() => { setFormData(prev => ({ ...prev, category_id: category.id })); setIsCategoryOpen(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors ${
                                    formData.category_id === category.id ? 'bg-primary/20 text-primary' : 'text-card-foreground'
                                  }`}
                                >
                                  {category.name_en}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Number of Servings */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
                        {t('recipes.numberOfServings')}
                      </label>
                      <input
                        type="number"
                        name="servings"
                        value={formData.servings}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                          errors.servings ? 'border-destructive bg-destructive/10' : 'border-border hover:border-primary'
                        }`}
                        placeholder="e.g., 4"
                        min="1"
                      />
                      {errors.servings && (
                        <p className="text-sm text-destructive flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.servings}
                        </p>
                      )}
                    </div>

                    {/* Portion Size */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
                        {t('recipes.portionSize')}
                      </label>
                      <input
                        type="number"
                        name="portion_size"
                        value={formData.portion_size}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                          errors.portion_size ? 'border-destructive bg-destructive/10' : 'border-border hover:border-primary'
                        }`}
                        placeholder="e.g., 250"
                        min="0"
                        step="0.01"
                      />
                      {errors.portion_size && (
                        <p className="text-sm text-destructive flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.portion_size}
                        </p>
                      )}
                    </div>

                    {/* Unit */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
{t('recipeForm.portionUnit')}
                      </label>
                      <div className="relative" ref={unitDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsUnitOpen(!isUnitOpen)}
                          className="w-full px-4 py-3 border border-border rounded-lg bg-background hover:border-primary focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-left flex items-center justify-between"
                        >
                          <span className={`${formData.unit ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {formData.unit || t('recipeForm.unit')}
                          </span>
                          <svg className={`w-5 h-5 text-muted-foreground transition-transform ${isUnitOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isUnitOpen && (
                          <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            <div className="py-2">
                              <button
                                type="button"
                                onClick={() => { setFormData(prev => ({ ...prev, unit: '' })); setIsUnitOpen(false); }}
                                className="w-full text-left px-4 py-2 text-sm text-card-foreground hover:bg-muted transition-colors"
                              >
                                Select unit
                              </button>
                              {unitOptions.map(unit => (
                                <button
                                  key={unit}
                                  type="button"
                                  onClick={() => { setFormData(prev => ({ ...prev, unit })); setIsUnitOpen(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors ${
                                    formData.unit === unit ? 'bg-primary/20 text-primary' : 'text-card-foreground'
                                  }`}
                                >
                                  {unit}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Holding Temperature */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
{t('recipeForm.holdingTemperature')}
                      </label>
                      <input
                        type="number"
                        name="holding_temperature"
                        value={formData.holding_temperature}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground ${
                          errors.holding_temperature ? 'border-destructive bg-destructive/10' : 'border-border hover:border-primary'
                        }`}
                        placeholder="e.g., 65"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      {errors.holding_temperature && (
                        <p className="text-sm text-destructive flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors.holding_temperature}
                        </p>
                      )}
                    </div>

                    {/* Has Pricing */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-card-foreground">
{t('recipeForm.hasPricing')}
                      </label>
                      <div className="flex items-start space-x-3">
                        <div className="flex items-center h-5">
                          <div 
                            className={`relative inline-flex items-center justify-center w-4 h-4 border-2 rounded cursor-pointer transition-colors ${
                              formData.has_pricing 
                                ? 'bg-primary border-primary' 
                                : 'bg-background border-border hover:border-primary'
                            }`}
                            onClick={() => {
                              const newValue = !formData.has_pricing;
                              console.log('Custom checkbox clicked, current value:', formData.has_pricing, 'new value:', newValue);
                              setFormData(prev => {
                                const updated = { ...prev, has_pricing: newValue };
                                console.log('Updated formData:', updated);
                                return updated;
                              });
                            }}
                          >
                            {formData.has_pricing && (
                              <svg 
                                className="w-3 h-3 text-primary-foreground" 
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                              >
                                <path 
                                  fillRule="evenodd" 
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                  clipRule="evenodd" 
                                />
                              </svg>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            id="has_pricing"
                            name="has_pricing"
                            checked={formData.has_pricing}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              console.log('Hidden checkbox triggered, current value:', formData.has_pricing, 'new value:', newValue);
                              setFormData(prev => ({ ...prev, has_pricing: newValue }));
                            }}
                            className="sr-only"
                            tabIndex={-1}
                          />
                        </div>
                        <div className="flex-1">
                          <label 
                            htmlFor="has_pricing" 
                            className="text-sm font-medium text-card-foreground cursor-pointer"
                            onClick={() => {
                              const newValue = !formData.has_pricing;
                              console.log('Label clicked, current value:', formData.has_pricing, 'new value:', newValue);
                              setFormData(prev => ({ ...prev, has_pricing: newValue }));
                            }}
                          >
{t('recipeForm.enablePricing')}
                          </label>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formData.has_pricing 
                              ? t('recipeForm.enablePricingDescription')
                              : t('recipeForm.disablePricingDescription')
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


                {/* Image Upload Card */}
                <div className="bg-card rounded-xl shadow-sm border border-border p-4 max-w-4xl mx-auto">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-chart-2/20 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-chart-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-card-foreground">{t('recipeForm.recipeImage')}</h4>
                      <p className="text-sm text-muted-foreground">{t('recipeForm.addVisualRepresentation')}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Image Preview */}
                    {imagePreview && (
                      <div className="flex items-start space-x-6 p-4 bg-muted/50 rounded-lg border-2 border-dashed border-border">
                        <div className="relative">
                          <img
                            src={imagePreview}
                            alt={t('recipeForm.recipePreview')}
                            className="w-32 h-32 object-cover rounded-lg shadow-sm"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1.5 hover:bg-destructive/90 transition-colors shadow-lg"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium text-card-foreground mb-2">{t('recipeForm.currentImage')}</h5>
                          <p className="text-sm text-muted-foreground mb-3">
                            {selectedFile 
                              ? t('recipeForm.imageSelected') 
                              : t('recipeForm.imageUploadedSuccessfully')}
                          </p>
                          <p className="text-xs text-muted-foreground">{t('recipeForm.replaceImageDescription')}</p>
                        </div>
                      </div>
                    )}

                    {/* Upload Controls */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-4">
                        <label 
                          className={`cursor-pointer inline-flex items-center px-6 py-3 border border-border rounded-lg shadow-sm text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all ${
                            uploading || imagePreview ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary'
                          }`}
                          onClick={(e) => {
                            // Ensure the file input click is not prevented by modal handlers
                            e.stopPropagation();
                          }}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {t('common.uploadImage')}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                            disabled={uploading}
                            onClick={(e) => {
                              // Allow the file input to open the file dialog
                              e.stopPropagation();
                            }}
                          />
                        </label>
                        {uploading && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                            {t('common.uploading')}
                          </div>
                        )}
                      </div>
                      
                      {/* Upload Info */}
                      <div className="bg-chart-1/10 border border-chart-1/20 rounded-lg p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-chart-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-chart-1">{t('recipeForm.imageRequirements')}</h3>
                            <div className="mt-2 text-sm text-chart-1">
                              <ul className="list-disc pl-5 space-y-1">
                                <li><strong>{t('recipeForm.formats')}:</strong> JPG, PNG, GIF</li>
                                <li><strong>{t('recipeForm.maximumSize')}:</strong> 2MB</li>
                                <li><strong>{t('recipeForm.recommended')}:</strong> {t('recipeForm.squareImagesRatio')}</li>
                                <li><strong>{t('recipeForm.quality')}:</strong> {t('recipeForm.highResolutionImages')}</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* Ingredients Tab */}
            {activeTab === 'ingredients' && (
              <div 
                ref={ingredientsTabRef}
                className="tab-panel is-active px-8 pb-8 pt-0 flex flex-col"
              >
              <div className="max-w-7xl mx-auto w-full">
                {/* Getting Started Message */}
                {!recipe && formData.ingredients.length === 0 && (
                  <div className="bg-chart-1/10 border border-chart-1/20 rounded-lg p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-chart-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-chart-1">
                          {t('recipeForm.addAtLeastOneIngredientToCreateDish')} 
                          {ingredientMode === 'dropdown' 
                            ? t('recipeForm.useFormAboveToAddIngredients')
                            : t('recipeForm.dragIngredientsFromLibraryToAddThem')
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Page Header */}
                {/* Mode Toggle Button - Hidden since we only use Library Form mode */}
                {/* <div className="flex items-center justify-center mb-4">
                    <div className="bg-gray-100 rounded-lg p-1 flex">
                    <button
                      type="button"
                      onClick={() => setIngredientMode('dropdown')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        ingredientMode === 'dropdown'
                          ? 'bg-white text-primary-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <svg className="w-3 h-3 mr-1.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Dropdown Form
                    </button>
                    <button
                      type="button"
                      onClick={() => setIngredientMode('dragdrop')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        ingredientMode === 'dragdrop'
                          ? 'bg-white text-primary-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <svg className="w-3 h-3 mr-1.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      Library Form
                    </button>
                  </div>
                </div> */}

                {/* Mobile source panel toggle - only show in dragdrop mode */}
                {ingredientMode === 'dragdrop' && (
                  <div className="mb-6 lg:hidden">
                    <button
                      type="button"
                      onClick={() => setShowSourcePanelMobile(v => !v)}
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-sm font-medium text-card-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                    >
{showSourcePanelMobile ? t('recipeForm.hideDirectMaterialsLibrary') : t('recipeForm.showDirectMaterialsLibrary')}
                    </button>
                  </div>
                )}

                <div className={`${ingredientMode === 'dragdrop' ? 'space-y-4' : 'grid grid-cols-1 gap-8 lg:grid-cols-1'}`}>
                  {/* Ingredients Library - only show in dragdrop mode */}
                  {ingredientMode === 'dragdrop' && (
                    <div className={`${showSourcePanelMobile ? 'block' : 'hidden lg:block'}`}>
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                      {/* Library Header */}
                      <div className="p-4 border-b border-border">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-lg font-semibold text-card-foreground">{t('recipeForm.ingredientsLibrary')}</h4>
                          <div className="relative w-96">
                            <div className={`absolute inset-y-0 ${i18n.language === 'ar' ? 'right-0 pr-5' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                              <Search className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <input
                              type="text"
                              value={materialSearchTerm}
                              onChange={(e) => setMaterialSearchTerm(e.target.value)}
                              placeholder={
                                isRecipesView 
                                   ? t('recipeForm.searchByDishNameOrId')
                                  : isAllMaterialsView 
                                    ? t('recipeForm.searchByMaterialNameOrId') 
                                    : t('recipeForm.searchByCategoryNameOrId')
                              }
                              className={`w-full py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-background text-foreground ${i18n.language === 'ar' ? 'pr-20 pl-5' : 'pl-10 pr-4'}`}
                            />
                          </div>
                        </div>
                        
                        {/* View Toggle Buttons */}
                        <div className="flex space-x-1 bg-muted rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => { setIsAllMaterialsView(false); setIsRecipesView(false); }}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              (!isAllMaterialsView && !isRecipesView) 
                                ? 'bg-card text-primary shadow-sm' 
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
{t('recipeForm.allCategories')}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setIsAllMaterialsView(true); setIsRecipesView(false); }}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              isAllMaterialsView 
                                ? 'bg-card text-primary shadow-sm' 
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
{t('recipeForm.allMaterials')}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setIsAllMaterialsView(false); setIsRecipesView(true); }}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              isRecipesView 
                                ? 'bg-card text-primary shadow-sm' 
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
{t('recipeForm.allDishes')}
                          </button>
                        </div>
                      </div>

                      {/* Library Content */}
                      <div className="h-48 overflow-y-auto p-2">
                        {isRecipesView ? (
                          <div className="divide-y divide-border">
                            {filteredRecipesList.map(rec => (
                              <div key={rec.id} className="p-3 hover:bg-muted transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium text-card-foreground truncate ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                                      {i18n.language === 'ar' ? (rec.name_ar || rec.name_en) : rec.name_en}
                                    </p>
                                    <p className="text-xs text-muted-foreground">ID: {rec.id}</p>
                                  </div>
                                  <button
                                    onClick={() => handleMaterialSelect({ id: rec.id, name: rec.name_en, type: 'recipe' })}
                                    className="inline-flex items-center px-3 py-1.5 border border-border rounded-md text-xs font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                                  >
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
{t('recipeForm.select')}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (!isAllMaterialsView ? (
                          <div className="p-4 space-y-3">
                            {/* Material Categories */}
                            {filteredMaterialCategories.map(cat => {
                              const isOpen = openCategoryIds.has(`material-${cat.id}`);
                              const catMaterials = filteredMaterials.filter(m => m.category_id === cat.id);
                              return (
                                <div key={`material-${cat.id}`} className="border border-border rounded-lg overflow-hidden">
                                  <button 
                                    type="button" 
                                    onClick={() => toggleCategoryOpen(`material-${cat.id}`)} 
                                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors bg-muted/50"
                                  >
                                    <div className={`flex flex-col items-start ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                                      <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                        <span className={`text-sm font-medium text-card-foreground ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                                          {i18n.language === 'ar' ? (cat.name_ar || cat.name_en) : cat.name_en}
                                        </span>
                                        <span className="text-xs text-chart-1 bg-chart-1/10 px-2 py-0.5 rounded-full">{t('recipeForm.materials')}</span>
                                      </div>
                                      <span className="text-xs text-muted-foreground mt-1">ID: {cat.id}</span>
                                    </div>
                                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                  </button>
                                  {isOpen && (
                                    <div className="border-t border-border bg-card">
                                      {catMaterials.length === 0 ? (
                                        <div className="px-4 py-3 text-xs text-muted-foreground text-center">{t('materials.noMaterialsInCategory')}</div>
                                      ) : (
                                        <div className="divide-y divide-border">
                                          {catMaterials.map(mat => (
                                            <div key={mat.id} className="p-4 hover:bg-muted transition-colors">
                                              <div className="flex items-center justify-between">
                                                <div className="flex-1 min-w-0">
                                                  <p className={`text-sm font-medium text-card-foreground truncate ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                                                    {i18n.language === 'ar' ? (mat.name_ar || mat.name_en) : mat.name_en}
                                                  </p>
                                                  <p className="text-xs text-muted-foreground">ID: {mat.id}{mat.sub_unit ? ` • ${mat.sub_unit}` : ''}</p>
                                                </div>
                                                <button
                                                  onClick={() => handleMaterialSelect({ id: mat.id, name: mat.name_en, unit: mat.sub_unit, type: 'rawMaterial' })}
                                                  className="inline-flex items-center px-3 py-1.5 border border-border rounded-md text-xs font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                                                >
                                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                  </svg>
              {t('recipeForm.select')}
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            
                            {/* Recipe Categories */}
                            {filteredRecipeCategories.map(cat => {
                              const isOpen = openCategoryIds.has(`recipe-${cat.id}`);
                              const catRecipes = filteredRecipesList.filter(r => r.category_id === cat.id);
                              return (
                                <div key={`recipe-${cat.id}`} className="border border-border rounded-lg overflow-hidden">
                                  <button 
                                    type="button" 
                                    onClick={() => toggleCategoryOpen(`recipe-${cat.id}`)} 
                                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors bg-muted/50"
                                  >
                                    <div className={`flex flex-col items-start ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                                      <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                        <span className={`text-sm font-medium text-card-foreground ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                                          {i18n.language === 'ar' ? (cat.name_ar || cat.name_en) : cat.name_en}
                                        </span>
                                        <span className="text-xs text-chart-3 bg-chart-3/10 px-2 py-0.5 rounded-full">{t('common.dishes')}</span>
                                      </div>
                                      <span className="text-xs text-muted-foreground mt-1">ID: {cat.id}</span>
                                    </div>
                                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                  </button>
                                  {isOpen && (
                                    <div className="border-t border-border bg-card">
                                      {catRecipes.length === 0 ? (
                                         <div className="px-4 py-3 text-xs text-muted-foreground text-center">{t('recipeForm.noDishesInCategory')}</div>
                                      ) : (
                                        <div className="divide-y divide-border">
                                          {catRecipes.map(rec => (
                                            <div key={rec.id} className="p-3 hover:bg-muted transition-colors">
                                              <div className="flex items-center justify-between">
                                                <div className="flex-1 min-w-0">
                                                  <p className={`text-sm font-medium text-card-foreground truncate ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                                                    {i18n.language === 'ar' ? (rec.name_ar || rec.name_en) : rec.name_en}
                                                  </p>
                                                  <p className="text-xs text-muted-foreground">ID: {rec.id}</p>
                                                </div>
                                                <button
                                                  onClick={() => handleMaterialSelect({ id: rec.id, name: rec.name_en, type: 'recipe' })}
                                                  className="inline-flex items-center px-3 py-1.5 border border-border rounded-md text-xs font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                                                >
                                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                  </svg>
              {t('recipeForm.select')}
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="divide-y divide-border">
                            {filteredMaterials.map(mat => (
                              <div key={mat.id} className="p-4 hover:bg-muted transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium text-card-foreground truncate ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                                      {i18n.language === 'ar' ? (mat.name_ar || mat.name_en) : mat.name_en}
                                    </p>
                                    <p className="text-xs text-muted-foreground">ID: {mat.id}{mat.sub_unit ? ` • ${mat.sub_unit}` : ''}</p>
                                  </div>
                                  <button
                                    onClick={() => handleMaterialSelect({ id: mat.id, name: mat.name_en, unit: mat.sub_unit, type: 'rawMaterial' })}
                                    className="inline-flex items-center px-3 py-1.5 border border-border rounded-md text-xs font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                                  >
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
{t('recipeForm.select')}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>

                      {/* Library Footer */}
                      <div className="p-4 border-t border-border bg-muted/50">
                        <p className="text-xs text-muted-foreground text-center">
                          {t('recipeForm.tipClickSelect')}
                        </p>
                      </div>
                    </div>
                    </div>
                  )}

                  {/* Selected Ingredients - show in dragdrop mode OR when editing existing recipe */}
                  {ingredientMode === 'dragdrop' && (
                    <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-card-foreground">{t('recipeForm.selectedIngredients')}</h4>
                        {formData.ingredients.length > 0 && (
                          <div className="flex items-center space-x-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-chart-3/10 text-chart-3">
                              {t('recipeForm.totalCost')}: {formatCurrency(formData.ingredients.reduce((total, ingredient) => {
                                const quantity = parseFloat(ingredient.quantityUsed || 0);
                                const wastePercentage = parseFloat(ingredient.wastePercentage || 0);
                                const effectiveQuantity = quantity * (1 + wastePercentage / 100);
                                
                                if (ingredient.type === 'rawMaterial') {
                                  const material = materials.find(m => m.id === ingredient.code);
                                  if (!material) return total;
                                  const unitCost = calculateUnitCost(material);
                                  return total + unitCost * effectiveQuantity;
                                } else {
                                  const recipe = recipes.find(r => r.id === ingredient.code);
                                  if (!recipe) return total;
                                  const costPerServing = calculateRecipeCostPerServing(recipe);
                                  return total + costPerServing * effectiveQuantity;
                                }
                              }, 0))}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {formData.ingredients.length} {formData.ingredients.length !== 1 ? t('recipeForm.ingredients') : t('recipeForm.ingredient')}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {formData.ingredients.length > 0 ? (
                        <div className="space-y-2">
                          {formData.ingredients.map((ingredient) => (
                            <IngredientItem
                              key={ingredient.id}
                              ingredient={ingredient}
                              onRemove={() => removeIngredient(ingredient.id)}
                              onEdit={() => openEditQuantity(ingredient)}
                              isNew={lastAddedId === ingredient.id}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                          <p className="text-muted-foreground text-sm">{t('recipeForm.noIngredientsAddedYet')}</p>
                          <p className="text-muted-foreground text-xs mt-1">{t('recipeForm.clickSelectToAddIngredients')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recipe Ingredients Management - only show in dropdown mode */}
                  <div className={`space-y-4 ${ingredientMode === 'dropdown' ? 'lg:col-span-1' : 'hidden'}`}>
                    {/* Add Ingredient Form - only show in dropdown mode */}
                    {ingredientMode === 'dropdown' && (
                      <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                        <h4 className="text-lg font-semibold text-card-foreground mb-3">{t('recipeForm.addAnIngredient')}</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div className="space-y-2">
                             <label className="block text-sm font-medium text-card-foreground">{t('recipeForm.materialsDishes')}</label>
                            <div className="relative" ref={ingredientCodeDropdownRef}>
                              <button
                                type="button"
                                onClick={() => setIsIngredientCodeOpen(!isIngredientCodeOpen)}
                                className="w-full px-4 py-3 border border-border rounded-lg bg-background hover:border-primary focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-left flex items-center justify-between"
                              >
                                <span className={`${newIngredient.code ? 'text-foreground' : 'text-muted-foreground'}`}>
{newIngredient.code ? (() => {
  const item = getAvailableItems().find(i => i.id === newIngredient.code && i.type === newIngredient.type);
  return item ? (i18n.language === 'ar' ? (item.name_ar || item.name) : item.name) : t('recipeForm.selected');
})() : t('recipeForm.selectIngredient')}
                                </span>
                                <svg className={`w-5 h-5 text-muted-foreground transition-transform ${isIngredientCodeOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {isIngredientCodeOpen && (
                                <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                  <div className="py-2">
                                    <button
                                      type="button"
                                      onClick={() => { setNewIngredient(prev => ({ ...prev, code: '' })); setIsIngredientCodeOpen(false); }}
                                      className="w-full text-left px-4 py-2 text-sm text-card-foreground hover:bg-muted transition-colors"
                                    >
{t('recipeForm.selectIngredient')}
                                    </button>
                                    {getAvailableItems().map(item => (
                                      <button
                                        key={`${item.type}:${item.id}`}
                                        type="button"
                                        onClick={() => { setNewIngredient(prev => ({ ...prev, code: item.id, type: item.type })); setIsIngredientCodeOpen(false); }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-muted text-card-foreground"
                                      >
                                        <span className={i18n.language === 'ar' ? 'font-arabic' : ''}>
                                          {i18n.language === 'ar' ? (item.name_ar || item.name) : item.name}
                                        </span> <span className={item.type === 'rawMaterial' ? 'text-chart-1' : 'text-chart-3'}>({item.type === 'rawMaterial' ? t('recipeForm.material') : t('recipeForm.recipe')})</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-card-foreground">{t('recipes.quantityUsed')}</label>
                            <input
                              type="number"
                              name="quantityUsed"
                              value={newIngredient.quantityUsed}
                              onChange={handleIngredientChange}
                              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground"
placeholder={t('recipeForm.enterQuantity')}
                              step="0.01"
                              min="0"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-card-foreground">
{t('recipeForm.wastePercentage')}
                              <span className="text-xs text-muted-foreground ml-1">{t('recipeForm.wastePercentageDescription')}</span>
                            </label>
                            <input
                              type="number"
                              name="wastePercentage"
                              value={newIngredient.wastePercentage}
                              onChange={handleIngredientChange}
                              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground"
placeholder={t('recipeForm.enterWastePercentage')}
                              step="0.1"
                              min="0"
                              max="100"
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={addIngredient}
                              className={`w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-primary-foreground transition-colors ${
                                formData.ingredients.length === 0 
                                  ? 'bg-primary hover:bg-primary/90 focus:ring-2 focus:ring-offset-2 focus:ring-primary' 
                                  : 'bg-primary hover:bg-primary/90 focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                              }`}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {formData.ingredients.length === 0 
? t('recipeForm.addFirstIngredient') 
                                : t('recipes.addIngredient')
                              }
                            </button>
                          </div>
                        </div>

                        {/* Selected Ingredients List - always show in dropdown mode */}
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="text-md font-semibold text-card-foreground">{t('recipeForm.selectedIngredients')}</h5>
                            {formData.ingredients.length > 0 && (
                              <div className="flex items-center space-x-3">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-chart-3/10 text-chart-3">
{t('recipeForm.totalCost')}: {formatCurrency(formData.ingredients.reduce((total, ingredient) => {
                                    const quantity = parseFloat(ingredient.quantityUsed || 0);
                                    const wastePercentage = parseFloat(ingredient.wastePercentage || 0);
                                    const effectiveQuantity = quantity * (1 + wastePercentage / 100);
                                    
                                    if (ingredient.type === 'rawMaterial') {
                                      const material = materials.find(m => m.id === ingredient.code);
                                      if (!material) return total;
                                      const unitCost = calculateUnitCost(material);
                                      return total + unitCost * effectiveQuantity;
                                    } else {
                                      const recipe = recipes.find(r => r.id === ingredient.code);
                                      if (!recipe) return total;
                                      const costPerServing = calculateRecipeCostPerServing(recipe);
                                      return total + costPerServing * effectiveQuantity;
                                    }
                                  }, 0))}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
{formData.ingredients.length} {formData.ingredients.length !== 1 ? t('recipeForm.ingredients') : t('recipeForm.ingredient')}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {formData.ingredients.length > 0 ? (
                            <div className="space-y-2">
                              {formData.ingredients.map((ingredient) => (
                                <IngredientItem
                                  key={ingredient.id}
                                  ingredient={ingredient}
                                  onRemove={() => removeIngredient(ingredient.id)}
                                  onEdit={() => openEditQuantity(ingredient)}
                                  isNew={lastAddedId === ingredient.id}
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              <p className="text-muted-foreground mb-2">{t('recipeForm.noIngredientsAddedYet')}</p>
                              <p className="text-sm text-muted-foreground">{t('recipeForm.addFirstIngredientDescription')}</p>
                            </div>
                          )}
                        </div>

                        {/* Error Message */}
                        {errors.ingredients && (
                          <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-destructive" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm text-destructive">{errors.ingredients}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}


                    {/* Help Text for New Recipes */}
                    
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* Procedure Tab */}
            {activeTab === 'procedure' && (
              <div 
                ref={procedureTabRef}
                className="tab-panel is-active p-6 flex flex-col"
              >
              <div className="max-w-7xl mx-auto w-full space-y-4">
                
                {/* Add Procedure Steps */}
                <div className="bg-card rounded-xl shadow-sm border border-border p-4">
                  <h4 className="text-lg font-semibold text-card-foreground mb-4">{t('recipeForm.addProcedureSteps')}</h4>
                  
                  <div className="space-y-4">
                    {/* Add New Step Form */}
                    <div className="space-y-3">
                      {/* Instruction Field and Image Upload */}
                      <div className={`flex items-start ${i18n.language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                        {/* Instruction Field */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-card-foreground mb-2">
                            {t('recipeForm.instruction')} <span className="text-destructive">*</span>
                          </label>
                          <textarea
                            value={newStepInstruction}
                            onChange={(e) => setNewStepInstruction(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors bg-background text-foreground"
                            rows={1}
                            placeholder={t('recipeForm.enterStepInstruction')}
                          />
                        </div>

                        {/* Image Upload + Preview */}
                        <div className="flex-shrink-0 flex flex-col items-start gap-2">
                          <label className="block text-sm font-medium text-card-foreground">
                            {t('recipeForm.stepImage')}
                          </label>
                          <div className="flex items-center gap-3 flex-wrap">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (!file) return;

                              // Validate file type
                              if (!file.type.startsWith('image/')) {
                                setErrorMessage(t('common.pleaseSelectImageFile'));
                                setErrorModalOpen(true);
                                e.target.value = ''; // Reset file input
                                return;
                              }

                              // Validate file size (2MB limit)
                              if (file.size > 2 * 1024 * 1024) {
                                setErrorMessage(t('common.imageSizeLimit'));
                                setErrorModalOpen(true);
                                e.target.value = ''; // Reset file input
                                return;
                              }

                              // Store the file for later upload (when Add Step is clicked)
                              setNewStepImage(file);

                              // Create a local preview using FileReader
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setNewStepImagePreview(reader.result); // This will be a data URL for preview
                              };
                              reader.onerror = () => {
                                setErrorMessage(t('common.failedToPreviewImage'));
                                setErrorModalOpen(true);
                              };
                              reader.readAsDataURL(file);
                              
                              console.log('📁 New step image file selected for preview:', file.name);
                              console.log('📁 Upload will happen when Add Step button is clicked');
                            }}
                            className="hidden"
                            id="new-step-image-upload"
                          />
                          <label
                            htmlFor="new-step-image-upload"
                            className={`inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${
                              newStepImagePreview ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <Upload className={`h-4 w-4 ${i18n.language === 'ar' ? 'ml-1' : 'mr-1'}`} />
                            {t('common.uploadImage')}
                          </label>
                            {newStepImagePreview && (
                              <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border border-border">
                                <img
                                  src={newStepImagePreview}
                                  alt={t('recipeForm.stepPreview')}
                                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewStepImage(null);
                                    setNewStepImagePreview(null);
                                  }}
                                  className="text-destructive hover:text-destructive/80 text-sm font-medium p-1"
                                  title={t('recipeForm.removeImage')}
                                >
                                  {t('recipeForm.removeImage')}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Add Step Button - step image will be uploaded when dish form Save is clicked */}
                      <div>
                                              <button
                        type="button"
                        onClick={() => {
                          if (!newStepInstruction.trim()) {
                            setErrorMessage(t('recipeForm.pleaseEnterInstruction'));
                            setErrorModalOpen(true);
                            return;
                          }

                          const newStepId = Date.now().toString(36) + Math.random().toString(36).substr(2);
                          const newStep = {
                            id: newStepId,
                            step_number: formData.procedures.length + 1,
                            instruction_en: newStepInstruction.trim(),
                            instruction_ar: '',
                            image_url: '', // Will be set when dish form is saved (backend uploads)
                            _previewUrl: newStepImage ? URL.createObjectURL(newStepImage) : undefined // Show preview until saved
                          };

                          if (newStepImage) {
                            pendingStepImagesRef.current[newStepId] = newStepImage;
                          }

                          setFormData(prev => ({
                            ...prev,
                            procedures: [...prev.procedures, newStep]
                          }));

                          setNewStepInstruction('');
                          setNewStepImage(null);
                          setNewStepImagePreview(null);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" />
{t('recipeForm.addStep')}
                      </button>
                      </div>
                    </div>

                    {/* Steps List - Only show when steps exist */}
                    {formData.procedures.length > 0 ? (
                      <div className="border-t border-border pt-6">
                        <div className="flex items-center justify-between mb-6">
                          <h5 className="text-lg font-semibold text-card-foreground">{t('recipeForm.steps')} ({formData.procedures.length})</h5>
                        </div>
                        
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={formData.procedures.map(step => step.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-2">
                              {formData.procedures.map((step, index) => (
                                <SortableStep
                                  key={step.id}
                                  step={step}
                                  index={index}
                                  editingStep={editingStep}
                                  editStepInstruction={editStepInstruction}
                                  setEditStepInstruction={setEditStepInstruction}
                                  editStepImage={editStepImage}
                                  setEditStepImage={setEditStepImage}
                                  editStepImagePreview={editStepImagePreview}
                                  setEditStepImagePreview={setEditStepImagePreview}
                                  uploadingEditStepImage={uploadingEditStepImage}
                                  setUploadingEditStepImage={setUploadingEditStepImage}
                                  setFormData={setFormData}
                                  setEditingStep={setEditingStep}
                                  onViewFullImage={handleViewFullImage}
                                  cleanupProcedureImage={cleanupProcedureImage}
                                  editStepImagePublicId={editStepImagePublicId}
                                  setEditStepImagePublicId={setEditStepImagePublicId}
                                  originalEditStepImagePublicId={originalEditStepImagePublicId}
                                  setOriginalEditStepImagePublicId={setOriginalEditStepImagePublicId}
                                  editStepImageRemoved={editStepImageRemoved}
                                  setEditStepImageRemoved={setEditStepImageRemoved}
                                  onRequestDeleteStep={setStepToDelete}
                                  pendingStepImagesRef={pendingStepImagesRef}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    ) : (
                      <div className="border-t border-border pt-6">
                        <div className="text-center py-8">
                          <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                          <p className="text-muted-foreground mb-2">{t('recipeForm.noProcedureStepsAddedYet')}</p>
                          <p className="text-sm text-muted-foreground">{t('recipeForm.addStepsUsingFormAbove')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>


                {/* Error Message */}
                {errors.procedures && (
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-destructive">
                          {t('recipeForm.procedureError')}
                        </h3>
                        <div className="mt-2 text-sm text-destructive">
                          <p>{errors.procedures}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Image Modal */}
            {imageModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">{t('recipeForm.stepImageModal')}</h3>
                    <button
                      type="button"
                      onClick={() => {
                        setImageModalOpen(false);
                        setModalImageUrl('');
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-4">
                    <img
                      src={modalImageUrl}
                      alt="Step image"
                      className="max-w-full max-h-[70vh] object-contain mx-auto"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="form-actions flex items-center justify-between pt-6 pb-2 px-6 border-t border-border bg-card">
            <div className={`mt-5 ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
              {activeTab === 'ingredients' && (
                <button 
                  type="button" 
                  onClick={() => setActiveTab('basic')} 
                  className="inline-flex items-center px-6 py-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  {i18n.language === 'ar' ? (
                    <>
                      {t('recipeForm.backToRecipeOverview')}
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      {t('recipeForm.backToRecipeOverview')}
                    </>
                  )}
                </button>
              )}
              {activeTab === 'procedure' && (
                <button 
                  type="button" 
                  onClick={() => setActiveTab('ingredients')} 
                  className="inline-flex items-center px-6 py-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  {i18n.language === 'ar' ? (
                    <>
                      {t('recipeForm.backToIngredients')}
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      {t('recipeForm.backToIngredients')}
                    </>
                  )}
                </button>
              )}
              {activeTab === 'basic' && (
                <button 
                  type="button" 
                  onClick={() => setActiveTab('ingredients')} 
                  className="inline-flex items-center px-6 py-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  {i18n.language === 'ar' ? (
                    <>
                      {t('recipeForm.nextIngredients')}
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {t('recipeForm.nextIngredients')}
                    </>
                  )}
                </button>
              )}
              {activeTab === 'ingredients' && (
                <button 
                  type="button" 
                  onClick={() => setActiveTab('procedure')} 
                  className="inline-flex items-center px-6 py-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  {i18n.language === 'ar' ? (
                    <>
                      {t('recipeForm.nextProcedure')}
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {t('recipeForm.nextProcedure')}
                    </>
                  )}
                </button>
              )}
            </div>
            <div className={`mt-5 ${i18n.language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
              <button
                type="button"
                onClick={() => { 
                  // Don't await cleanup - let it run in background
                  cleanupUploadedImage();
                  clearPendingStepImages();
                  setIsModalOpen(false); 
                  onCancel(); 
                }}
                className="inline-flex items-center justify-center w-32 px-6 py-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
                disabled={uploading}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center w-32 px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-primary-foreground transition-colors ${
                  uploading 
                    ? 'bg-muted cursor-not-allowed' 
                    : 'bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                }`}
                disabled={uploading}
                onClick={(e) => {
                  console.log('Recipe save button clicked!');
                  console.log('Button disabled:', uploading);
                  console.log('Button type:', 'submit');
                  console.log('Event:', e);
                  
                  // Manual form submission test
                  const form = e.target.closest('form');
                  console.log('Found form element:', form);
                  if (form) {
                    console.log('Form onSubmit handler:', form.onsubmit);
                    console.log('Form action:', form.action);
                    console.log('Form method:', form.method);
                    
                    // Try to manually trigger form submission
                    setTimeout(() => {
                      console.log('Attempting manual form submission...');
                      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                      form.dispatchEvent(submitEvent);
                    }, 100);
                  }
                }}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('common.save')}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Quantity Prompt Modal */}
      {quantityPromptOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-card w-full max-w-sm rounded-lg shadow-lg border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-card-foreground">{t('recipeForm.addIngredient')}</h4>
              <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setQuantityPromptOpen(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t('recipeForm.specifyQuantityFor')} <span className="font-medium text-card-foreground">{pendingIngredient?.name}</span>{pendingIngredient?.unit ? ` (${pendingIngredient.unit})` : ''}.</p>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-card-foreground">{t('recipeForm.quantity')}</label>
              <input
                type="number"
                value={quantityInput}
                onChange={(e) => setQuantityInput(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground"
                step="0.01"
                placeholder={t('recipeForm.quantityPlaceholder')}
                autoFocus
              />
              <label className="block text-sm font-medium text-card-foreground">
                {t('recipeForm.wastePercentage')}
                <span className="text-xs text-muted-foreground ml-1">{t('recipeForm.wastePercentageDescription')}</span>
              </label>
              <input
                type="number"
                value={wastePercentageInput}
                onChange={(e) => setWastePercentageInput(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground"
                step="0.1"
                min="0"
                max="100"
                placeholder={t('recipeForm.wastePercentagePlaceholder')}
              />
              <div className="text-xs text-muted-foreground">{t('recipeForm.youCanEditThisLater')}</div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button className="px-4 py-2 text-sm font-medium text-card-foreground bg-background border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary" onClick={() => setQuantityPromptOpen(false)}>{t('recipeForm.cancel')}</button>
              <button
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                onClick={() => {
                  const qty = parseFloat(quantityInput);
                  if (isNaN(qty) || qty <= 0) return;
                  const waste = parseFloat(wastePercentageInput) || 0;
                  confirmAddIngredient(pendingIngredient.code, pendingIngredient.type, String(qty), waste);
                  setQuantityPromptOpen(false);
                  setPendingIngredient(null);
                }}
              >
                {t('recipeForm.add')}
              </button>
            </div>
          </div>
        </div>
      )}
      {editQuantityModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-card w-full max-w-sm rounded-lg shadow-lg border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-card-foreground">{t('recipeForm.editQuantity')}</h4>
              <button className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setEditQuantityModalOpen(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t('recipeForm.updateQuantityFor')} <span className="font-medium text-card-foreground">{editTarget?.name}</span>{editTarget?.unit ? ` (${editTarget.unit})` : ''}.</p>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-card-foreground">{t('recipeForm.quantity')}</label>
              <input
                type="number"
                value={editQuantityInput}
                onChange={(e) => setEditQuantityInput(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground"
                step="0.01"
                placeholder={t('recipeForm.quantityPlaceholder')}
                autoFocus
              />
              <label className="block text-sm font-medium text-card-foreground">
                {t('recipeForm.wastePercentage')}
                <span className="text-xs text-muted-foreground ml-1">{t('recipeForm.wastePercentageDescription')}</span>
              </label>
              <input
                type="number"
                value={editWastePercentageInput}
                onChange={(e) => setEditWastePercentageInput(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-background text-foreground"
                step="0.1"
                min="0"
                max="100"
                placeholder={t('recipeForm.wastePercentagePlaceholder')}
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button className="px-4 py-2 text-sm font-medium text-card-foreground bg-background border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary" onClick={() => setEditQuantityModalOpen(false)}>{t('recipeForm.cancel')}</button>
              <button className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary" onClick={confirmEditQuantity}>{t('recipeForm.update')}</button>
            </div>
          </div>
        </div>
      )}
      {showDuplicateModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-card w-full max-w-sm rounded-lg shadow-lg border border-border p-6">
            <div className="flex items-center mb-6">
              <AlertCircle className="h-6 w-6 text-destructive mr-3" />
              <h2 className="text-lg font-semibold text-card-foreground">{t('recipeForm.duplicateIngredient')}</h2>
            </div>
            <div className="mb-8">
              <p className="text-card-foreground">{t('recipeForm.ingredientAlreadyAdded')}</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowDuplicateModal(false)}
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
{t('recipeForm.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
      {errorModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card w-full max-w-md rounded-xl shadow-2xl border border-border/50 p-8 mx-4 transform transition-all duration-300 ease-out scale-100">
            {/* Header with icon */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <h4 className="text-lg font-semibold text-card-foreground">{t('recipeForm.error')}</h4>
              </div>
              <button 
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted/50" 
                onClick={() => setErrorModalOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Message */}
            <div className="mb-8">
              <p className="text-sm text-muted-foreground leading-relaxed">{errorMessage}</p>
            </div>
            
            {/* Action button */}
            <div className="flex justify-end">
              <button
                onClick={() => setErrorModalOpen(false)}
                className="px-6 py-2.5 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors shadow-sm hover:shadow-md"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete step confirmation modal - themed */}
      {stepToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card w-full max-w-md rounded-xl shadow-2xl border border-border p-8 mx-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </div>
                <h4 className="text-lg font-semibold text-card-foreground">{t('recipeForm.delete')}</h4>
              </div>
              <button
                type="button"
                className="text-muted-foreground hover:text-card-foreground transition-colors p-1 rounded-md hover:bg-muted/50"
                onClick={() => setStepToDelete(null)}
                aria-label={t('common.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8">
              {t('recipeForm.areYouSureDeleteStep')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setStepToDelete(null)}
                className="px-4 py-2.5 text-sm font-medium text-card-foreground bg-background border border-border rounded-lg hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const step = stepToDelete;
                  setStepToDelete(null);
                  delete pendingStepImagesRef.current[step.id];
                  if (step._previewUrl) try { URL.revokeObjectURL(step._previewUrl); } catch (_) {}
                  setFormData(prev => ({
                    ...prev,
                    procedures: prev.procedures.filter(s => s.id !== step.id).map((s, idx) => ({
                      ...s,
                      step_number: idx + 1
                    }))
                  }));
                }}
                className="px-4 py-2.5 text-sm font-medium text-destructive-foreground bg-destructive border border-transparent rounded-lg hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive"
              >
                {t('recipeForm.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeForm; 